import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAllUsers } from "../lib/api";
import FollowButton from "./FollowButton";
import VerificationBadge from "./VerificationBadge";
import { useTranslation } from "../hooks/useTranslation";

const SUGGEST_LIMIT = 12;
const PLACEHOLDER_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' stop-color='%23e5e7eb'/%3E%3Cstop offset='100%25' stop-color='%23d1d5db'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='64' height='64' fill='url(%23g)' rx='32'/%3E%3Ccircle cx='32' cy='26' r='10' fill='%239ca3af'/%3E%3Cellipse cx='32' cy='58' rx='18' ry='14' fill='%239ca3af'/%3E%3C/svg%3E";

const SuggestFollowBox: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { data: users = [], isLoading } = useAllUsers(1, 25, "all", {
    enabled: isAuthenticated && !!currentUser?.id,
  });

  if (!isAuthenticated || !currentUser) return null;

  const suggested = (Array.isArray(users) ? users : [])
    .filter(
      (u: any) =>
        u?.id != null &&
        String(u.id) !== String(currentUser.id) &&
        !u.is_following
    )
    .slice(0, SUGGEST_LIMIT);

  if (isLoading || suggested.length === 0) return null;

  return (
    <section
      className="my-0 py-3 bg-white dark:bg-gray-800/95 border border-gray-100 dark:border-gray-700/80 shadow-sm overflow-hidden"
      aria-label={t("follow.suggestionsForYou", "Suggestions for you")}
    >
      <div className="overflow-x-auto scrollbar-hide scroll-smooth touch-pan-x pb-4 pt-3 -mx-1 px-1">
        <div className="flex gap-6 pl-3 pr-3 min-w-max">
          {suggested.map((u: any) => (
            <div
              key={u.id}
              className="flex-shrink-0 flex flex-col items-center gap-2.5 w-[100px] group"
            >
              <button
                type="button"
                onClick={() => navigate(`/${u.username || u.id}`)}
                className="relative focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 rounded-full p-[3px] bg-gray-200 dark:bg-gray-600 hover:bg-gradient-to-br hover:from-blue-400 hover:via-purple-400 hover:to-pink-400 transition-all duration-200 active:scale-95 hover:scale-[1.04]"
                aria-label={u.username || u.name || `user_${u.id}`}
              >
                <span className="block rounded-full overflow-hidden bg-white dark:bg-gray-800 ring-2 ring-white dark:ring-gray-800">
                  <img
                    src={u.image || PLACEHOLDER_AVATAR}
                    alt={u.name || u.username || 'پروفایل کاربر'}
                    width={56}
                    height={56}
                    loading="lazy"
                    decoding="async"
                    className="w-14 h-14 rounded-full object-cover bg-gray-200 dark:bg-gray-600"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_AVATAR;
                    }}
                  />
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate(`/${u.username || u.id}`)}
                className="flex items-center justify-center gap-1 text-xs font-medium text-gray-900 dark:text-white truncate max-w-full text-center hover:underline focus:outline-none focus-visible:underline rounded px-1 py-0.5 transition-colors text-gray-700 dark:text-gray-200"
              >
                <span className="truncate">
                  {u.username || u.name || `user_${u.id}`}
                </span>
                {u.verified && (
                  <span className="flex-shrink-0 inline-flex">
                    <VerificationBadge verified size="sm" />
                  </span>
                )}
              </button>

              <div className="w-full flex justify-center">
                <FollowButton
                  userId={String(u.id)}
                  username={u.username || u.name}
                  isFollowingFromList={false}
                  size="sm"
                  className="!min-w-0 w-full"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SuggestFollowBox;
